/*
 * Drives the mobile filter/sort panel (sections/product-grid.liquid,
 * snippets/aleo-filter-section.liquid) -- The Row-style: square chip
 * buttons that apply the instant they're tapped, no Apply button.
 *
 * Auto-apply works via the Section Rendering API: every change fetches
 * `{{ collection.url }}?<filter params>&section_id={{ section.id }}`
 * (set on #aleoFilterPanel's data attributes), which returns just this
 * section's own freshly-rendered HTML -- no full page, no navigation.
 * The new .aleo-grid__products / .aleo-grid__pagination / filter-panel
 * markup is parsed out of that response and swapped in. A real GET URL
 * (built the same way Shopify's own filter links work, with every
 * checked value's real param_name) is pushed via history.replaceState on
 * every change, so the result stays shareable/bookmarkable even though
 * nothing actually navigated. If the fetch ever fails, this falls back
 * to a real navigation to that same URL -- the "quick reload" fallback.
 *
 * Deliberately never replaces the .aleo-grid__products node itself, only
 * its innerHTML (and toggles the sibling .aleo-grid__empty message's
 * `hidden` attribute instead of swapping between them) -- assets/aleo-
 * grid-mobile-carousel.js (not touched by this file at all) attaches a
 * MutationObserver to that exact node once at load, and a
 * MutationObserver stops reporting anything forever the moment the node
 * it's watching is itself replaced rather than merely having its children
 * changed. Every event listener here is bound once, at load, to nodes
 * that are never themselves replaced (#aleoFilterPanel, #AleoFilterForm)
 * via delegation -- not to individual chips/buttons -- so it keeps
 * working no matter how many times this script rewrites what's inside
 * them.
 */
(function () {
  var panel = document.getElementById('aleoFilterPanel');
  var form = document.getElementById('AleoFilterForm');
  var trigger = document.getElementById('aleoFiltersTrigger');
  var shade = document.getElementById('aleoGridShade');
  var closeBtn = document.getElementById('aleoFilterClose');
  if (!panel || !form || !trigger || !shade || !closeBtn) return;

  var triggerText = trigger.querySelector('.aleo-grid__filters-trigger__text');
  var panelTitle = panel.querySelector('.aleo-grid__panel-title');
  var sectionId = panel.dataset.sectionId;
  var baseUrl = panel.dataset.collectionUrl;

  function openPanel() {
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    shade.classList.add('is-visible');
    trigger.setAttribute('aria-expanded', 'true');
  }
  function closePanel() {
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    shade.classList.remove('is-visible');
    trigger.setAttribute('aria-expanded', 'false');
  }
  trigger.addEventListener('click', function () {
    if (panel.classList.contains('is-open')) closePanel(); else openPanel();
  });
  closeBtn.addEventListener('click', closePanel);
  shade.addEventListener('click', closePanel);
  document.addEventListener('keydown', function (evt) {
    if (evt.key === 'Escape') closePanel();
  });

  // Delegated on #aleoFilterPanel itself (never replaced -- only the
  // .aleo-grid__panel-body form's innerHTML inside it is), so expand/
  // collapse and Reset keep working after every AJAX refresh without
  // being rebound.
  panel.addEventListener('click', function (evt) {
    var head = evt.target.closest('.aleo-filter-section__head');
    if (head) {
      var group = head.closest('.aleo-filter-section');
      group.dataset.open = group.dataset.open === 'true' ? 'false' : 'true';
      return;
    }
    if (evt.target.closest('.aleo-grid__panel-reset')) {
      form.querySelectorAll('.aleo-filter-chip__input:checked').forEach(function (input) {
        input.checked = false;
      });
      updateHeadingAndReset();
      applyFilters();
    }
  });

  // Delegated on #AleoFilterForm itself (never replaced -- only its
  // innerHTML is), so every chip/sort input keeps auto-applying after any
  // number of AJAX refreshes.
  form.addEventListener('change', function (evt) {
    if (evt.target.matches('.aleo-filter-chip__input, .aleo-filter-sort-input')) {
      // Optimistic: the badge/Reset-visibility reflect the tap instantly,
      // not only once the fetch below resolves -- updateHeadingAndReset()
      // runs again there too, as the authoritative value once the server
      // responds.
      updateHeadingAndReset();
      applyFilters();
    }
  });

  function collectOpenState() {
    var state = {};
    panel.querySelectorAll('.aleo-filter-section[data-key]').forEach(function (s) {
      state[s.dataset.key] = s.dataset.open;
    });
    return state;
  }
  function restoreOpenState(state) {
    panel.querySelectorAll('.aleo-filter-section[data-key]').forEach(function (s) {
      if (state[s.dataset.key] !== undefined) s.dataset.open = state[s.dataset.key];
    });
  }

  function countActive() {
    return form.querySelectorAll(
      '.aleo-filter-section[data-key]:not([data-key="sort"]) .aleo-filter-chip__input:checked'
    ).length;
  }

  function updateHeadingAndReset() {
    var n = countActive();
    var text = n > 0 ? 'Filters (' + n + ')' : 'Filters';
    if (triggerText) triggerText.textContent = text;
    if (panelTitle) panelTitle.textContent = text;
    var foot = form.querySelector('.aleo-grid__panel-foot');
    if (foot) foot.hidden = n === 0;
  }

  function buildParams() {
    var params = new URLSearchParams();
    form.querySelectorAll('.aleo-filter-chip__input:checked').forEach(function (input) {
      params.append(input.name, input.value);
    });
    var sortChecked = form.querySelector('.aleo-filter-sort-input:checked');
    if (sortChecked) params.append(sortChecked.name, sortChecked.value);
    return params;
  }

  var currentController = null;

  function applyFilters() {
    var params = buildParams();
    var openState = collectOpenState();
    var qs = params.toString();
    var navUrl = baseUrl + (qs ? '?' + qs : '');
    var fetchUrl = baseUrl + '?' + (qs ? qs + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);

    if (currentController) currentController.abort();
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    currentController = controller;

    fetch(fetchUrl, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('aleo-grid-filters: bad response status');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var newProducts = doc.querySelector('.aleo-grid__products');
        var liveProducts = document.querySelector('.aleo-grid__products');
        if (newProducts && liveProducts) {
          liveProducts.innerHTML = newProducts.innerHTML;
          liveProducts.hidden = newProducts.hidden;
        }

        var newEmpty = doc.querySelector('.aleo-grid__empty');
        var liveEmpty = document.querySelector('.aleo-grid__empty');
        if (newEmpty && liveEmpty) {
          liveEmpty.hidden = newEmpty.hidden;
        }

        var newPagination = doc.querySelector('.aleo-grid__pagination');
        var livePagination = document.querySelector('.aleo-grid__pagination');
        if (newPagination && livePagination) {
          livePagination.innerHTML = newPagination.innerHTML;
        }

        var newPanelBody = doc.querySelector('.aleo-grid__panel-body');
        if (newPanelBody) {
          form.innerHTML = newPanelBody.innerHTML;
          restoreOpenState(openState);
        }

        updateHeadingAndReset();
        history.replaceState(null, '', navUrl);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location = navUrl;
      });
  }

  updateHeadingAndReset();
})();
